import type { QueueEncounter } from '../types'
import { resolveQueryTypeFromSummary } from './query-type-labels'
import { writeStyledSheet } from '@/shared/utils/styled-excel-sheet'

const STATUS_DISPLAY: Record<string, string> = {
  received: 'Received',
  copilot: 'Copilot',
  autopilot: 'Autopilot',
  coding_complete: 'Coding Complete',
  exported: 'Exported',
  copilot_ready: 'Copilot',
  autopilot_ready: 'Autopilot',
}

const SLA_DISPLAY: Record<string, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  overdue: 'Overdue',
}

interface ColumnDef {
  key: string
  label: string
}

function formatDate(iso: string): string {
  if (typeof iso === 'string' && iso.includes('*')) return iso // already PHI-masked (e.g. **/**/2026)
  // A bare date-only string ('YYYY-MM-DD', e.g. date_of_service) has no timezone,
  // so read its digits literally instead of round-tripping through `new Date()` +
  // local getters — that path anchors it at UTC midnight and can render a day
  // earlier for any exporter behind UTC. Mirrors shared/utils/format-date.ts.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
}

function formatSlaTime(elapsedMinutes: number, overdueMinutes: number | null): string {
  if (overdueMinutes !== null) {
    const h = Math.floor(overdueMinutes / 60)
    const m = Math.floor(overdueMinutes % 60)
    return h > 0 ? `${h}h ${m}m overdue` : `${m}m overdue`
  }
  const h = Math.floor(elapsedMinutes / 60)
  const m = Math.floor(elapsedMinutes % 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/** Copilot / Autopilot, with an "(Edited)" suffix when the coder touched AI predictions.
 *  Mirrors the coding_mode contract in CodingModeEditedBadges and the history export. */
function codingModeLabel(enc: QueueEncounter): string {
  if (!enc.coding_mode) return ''
  const base = enc.coding_mode === 'copilot' ? 'Copilot' : 'Autopilot'
  return enc.was_edited ? `${base} (Edited)` : base
}

/** Maps a column key to a cell value for a given encounter */
function getCellValue(enc: QueueEncounter, key: string): string | number {
  switch (key) {
    case 'coding_mode':
      return codingModeLabel(enc)
    case 'encounter_id':
      return enc.id
    case 'patient_id':
      return enc.patient_id
    case 'patient_name':
      return enc.patient_name
    case 'claim_id':
      return enc.claim_id || ''
    case 'med_id':
      return enc.med_id || ''
    case 'specialty':
      return enc.specialty_display_name || enc.specialty
    case 'provider':
      return `${enc.provider.last_name}, ${enc.provider.first_name}`
    case 'received_on':
      return formatDate(enc.created_at)
    case 'source':
      return enc.source === 'manual' ? 'Manual' : 'RPA'
    case 'dos':
      return formatDate(enc.date_of_service)
    case 'facility':
      return enc.facility.name
    case 'report_type':
      return enc.report_type.display_name
    case 'status':
      return STATUS_DISPLAY[enc.status] || enc.status
    case 'sla':
      return `${SLA_DISPLAY[enc.sla_status] || enc.sla_status} (${formatSlaTime(enc.elapsed_minutes, enc.overdue_minutes)})`
    case 'confidence':
      return enc.confidence !== null ? `${enc.confidence}%` : ''
    case 'predicted_cpt_hcpcs':
      return [...new Set(enc.predicted_cpt_hcpcs ?? [])].join(', ')
    case 'predicted_icd':
      return [...new Set(enc.predicted_icd ?? [])].join(', ')
    case 'predicted_modifiers':
      return [...new Set(enc.predicted_modifiers ?? [])].join(', ')
    case 'selected_cpt_hcpcs':
      return [...new Set(enc.selected_cpt_hcpcs ?? [])].join(', ')
    case 'selected_icd':
      return [...new Set(enc.selected_icd ?? [])].join(', ')
    case 'selected_modifiers':
      return [...new Set(enc.selected_modifiers ?? [])].join(', ')
    case 'selected_cpt_icd_links':
      return (enc.selected_cpt_icd_links ?? []).join(' | ')
    case 'selected_cpt_modifier_links':
      return (enc.selected_cpt_modifier_links ?? []).join(' | ')
    case 'selected_hcpcs_icd_links':
      return (enc.selected_hcpcs_icd_links ?? []).join(' | ')
    case 'selected_hcpcs_modifier_links':
      return (enc.selected_hcpcs_modifier_links ?? []).join(' | ')
    case 'selected_final_summary':
      return (enc.selected_final_summary_units ?? enc.selected_final_summary ?? []).join(' | ')
    case 'selected_cpt_pi_indicator':
      return (enc.selected_cpt_pi_indicator ?? []).join(' | ')
    case 'payer':
      return enc.payer_name || ''
    case 'insurance_category':
      return enc.insurance_category || ''
    case 'query_type': {
      const resolved = enc.queries ? resolveQueryTypeFromSummary(enc.queries) : null
      if (!resolved) return ''
      const remainder = (enc.queries?.open_count ?? 0) > 1 ? (enc.queries!.open_count - 1) : 0
      return remainder > 0 ? `${resolved.label} (+${remainder} more)` : resolved.label
    }
    case 'query_reason': {
      const note = (enc.queries?.preview_note ?? '').trim()
      if (note) return note
      const total = enc.queries?.open_count ?? 0
      return total > 0 ? `${total} open` : ''
    }
    case 'is_edited':
      return enc.was_edited ? 'Yes' : 'No'
    case 'completed_by': {
      const cb = enc.completed_by
      if (!cb) return ''
      if (cb.first_name || cb.last_name) {
        return `${cb.last_name ?? ''}${cb.last_name && cb.first_name ? ', ' : ''}${cb.first_name ?? ''}`.trim()
      }
      return cb.email ?? ''
    }
    case 'completed_at':
      return enc.completed_at ? formatDate(enc.completed_at) : ''
    case 'flags':
      // One line per applied flag (not deduped): "Flag - Note" when a note exists.
      return (enc.flags ?? [])
        .map((f) => (f.note && f.note.trim().length > 0 ? `${f.name} - ${f.note}` : f.name))
        .join('; ')
    case 'modules': {
      // Distinct "Module / Submodule" pairs across the encounter's flags.
      const seen = new Set<string>()
      const items: string[] = []
      for (const f of enc.flags ?? []) {
        if (!f.module_name) continue
        const label = f.submodule_name ? `${f.module_name} / ${f.submodule_name}` : f.module_name
        if (!seen.has(label)) { seen.add(label); items.push(label) }
      }
      return items.join('; ')
    }
    case 'notes':
      return enc.notes ?? ''
    default:
      return ''
  }
}

// Always-included columns that are not part of the toggleable set
const FIXED_COLUMNS: ColumnDef[] = [
  { key: 'encounter_id', label: 'Chart ID' },
  { key: 'patient_id', label: 'Patient ID' },
  { key: 'patient_name', label: 'Patient Name' },
]

export async function exportToExcel(
  encounters: QueueEncounter[],
  visibleColumnKeys: Set<string>,
  allColumns: readonly ColumnDef[],
) {
  if (encounters.length === 0) return

  // Build ordered column list: fixed columns first, then visible toggleable columns.
  // med_id is shown to every role, but drop the column when no row carries one
  // (an all-empty column adds nothing).
  const hasMedId = encounters.some((e) => !!e.med_id)
  // FIXED_COLUMNS are always exported, so skip any toggleable column that
  // repeats one of them — patient_id appears in BOTH lists and is visible by
  // default, which used to emit two identical "Patient ID" columns. (SheetJS hid
  // it: rows were keyed objects, so the second write overwrote the first. ExcelJS
  // takes an ordered header array, which faithfully writes both.)
  const fixedKeys = new Set(FIXED_COLUMNS.map((c) => c.key))
  const columns = [
    ...FIXED_COLUMNS,
    ...allColumns.filter(
      (c) =>
        !fixedKeys.has(c.key) &&
        visibleColumnKeys.has(c.key) &&
        (c.key !== 'med_id' || hasMedId),
    ),
  ]

  // Build data rows
  const rows = encounters.map((enc) => {
    const row: Record<string, string | number> = {}
    for (const col of columns) {
      row[col.label] = getCellValue(enc, col.key)
    }
    return row
  })

  // Styled writer (exceljs) — brand-blue header, banded rows, real Excel table.
  // SheetJS was replaced here purely for styling: it cannot colour cells at all.
  // Columns, values, widths, sheet name and filename are unchanged.
  const dateStr = new Date().toISOString().split('T')[0]
  await writeStyledSheet({
    sheetName: 'Chart Queue',
    tableName: 'ChartQueue',
    columns: columns.map((col) => ({ label: col.label })),
    rows,
    fileName: `chart-queue-${dateStr}.xlsx`,
  })
}
